import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';

import { KickstartService } from '../../../../../core/kickstart/kickstart.service';
import {
  KsAccountConfirmation,
  KsAccountConfirmationAnswer,
  KsBusinessAccountConfirmation,
} from '../../../../../core/kickstart/kickstart.types';
import { IconComponent } from '../../../../../shared/icon/icon';
import { AccountRow, rowValid } from '../../create/ks-create';

const EMPTY_ROW: AccountRow = { bank_name: '', last_four: '', reason: '' };

/**
 * DOC-86 (FLOW-366, `FC-DET-106` limb 3) — once the offer is accepted, a person
 * at the business confirms its own account list, first among the confirmations.
 *
 * Shows the list as the record holds it, who gave the form's answer and in what
 * capacity, and the form's question with nothing pre-selected (UI-DA-86-02).
 * Required where an agent answered; shown as confirmed with *Update* where the
 * business did (UI-DA-86-03). The host renders it only where the back end says
 * the viewer may answer (UI-DA-86-04). A *No* posts the form's own answer, so
 * a named account runs the form's path (UI-DA-86-05). No fault words
 * (`FC-DET-047`).
 */
@Component({
  selector: 'app-ks-account-step',
  imports: [
    DatePipe,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatRadioModule,
    IconComponent,
  ],
  templateUrl: './ks-account-step.html',
  styleUrl: './ks-account-step.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KsAccountStepComponent {
  private readonly kickstart = inject(KickstartService);
  private readonly destroyRef = inject(DestroyRef);

  readonly shortId = input.required<string>();
  readonly step = input.required<KsBusinessAccountConfirmation>();
  /** The newest answer on the record — the form's, or the business's since. */
  readonly confirmation = input<KsAccountConfirmation | null>(null);
  /** Saved — the host re-reads the application. */
  readonly changed = output<void>();

  /** Nothing pre-selected (UI-DA-86-02). */
  protected readonly answer = signal<KsAccountConfirmationAnswer | null>(null);
  protected readonly rows = signal<AccountRow[]>([{ ...EMPTY_ROW }]);
  protected readonly updating = signal(false);
  protected readonly busy = signal(false);
  protected readonly error = signal<string | null>(null);

  /** The question is put while owed, or once the business chooses *Update*. */
  protected readonly asking = computed(() => this.step().owed || this.updating());

  protected readonly canSave = computed(() => {
    const answer = this.answer();
    if (answer === 'ALL_UPLOADED') return true;
    return answer === 'NOT_ALL' && this.rows().length > 0 && this.rows().every(rowValid);
  });

  protected updateRow(index: number, field: keyof AccountRow, value: string): void {
    this.rows.update((rows) => rows.map((r, i) => (i === index ? { ...r, [field]: value } : r)));
  }

  protected addRow(): void {
    this.rows.update((rows) => [...rows, { ...EMPTY_ROW }]);
  }

  protected save(): void {
    const answer = this.answer();
    if (!answer || !this.canSave()) return;
    const accounts =
      answer === 'NOT_ALL'
        ? this.rows().map((r) => ({ ...r, bank_name: r.bank_name.trim(), reason: r.reason.trim() }))
        : [];
    this.busy.set(true);
    this.error.set(null);
    this.kickstart
      .confirmAccounts(this.shortId(), answer, accounts)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.busy.set(false);
          this.updating.set(false);
          this.answer.set(null);
          this.rows.set([{ ...EMPTY_ROW }]);
          this.changed.emit();
        },
        error: (err) => {
          this.busy.set(false);
          this.error.set(err?.error?.detail ?? "We couldn't save your answer. Please try again.");
        },
      });
  }
}
